import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { Row } from 'reactstrap';
import { ApiProductCard, ApiWishlistProductsRequestFromJSON } from '../../api';
import useSearchApi from '../Util/useSearchApi';
import useThgConfig from '../Hooks/useThgConfig';
import { getWishlist, removeWishlistItems, subscribeToWishlist } from '../Util/wishlistStorage';
import ResultListItem from './ResultListItem';
import { useTranslation } from '@ibe/components';
import Keys from '../Translations/generated/wishlist-content.de.json.keys';
import fallback from '../Translations/generated/wishlist-content.de.json';

type WishlistStatus = 'loading' | 'ready' | 'empty' | 'error';

const Wishlist = (): JSX.Element | null => {
  const { t } = useTranslation('wishlist-content', fallback);
  const thgConfig = useThgConfig();

  const currentWishlistApi = useSearchApi();
  const wishlistApiRef = useRef(currentWishlistApi);

  useEffect(() => {
    wishlistApiRef.current = currentWishlistApi;
  }, [currentWishlistApi]);
  const wishlistEnabled = thgConfig.wishlistConfig?.isTurnedOn === true;

  const [products, setProducts] = useState<ApiProductCard[]>([]);
  const [status, setStatus] = useState<WishlistStatus>('loading');
  const [hasUnresolvedProducts, setHasUnresolvedProducts] = useState<boolean>(false);
  const requestSequence = useRef<number>(0);

  const loadWishlist = useCallback(async (): Promise<void> => {
    if (!wishlistEnabled) {
      return;
    }

    const requestId = ++requestSequence.current;
    const productIds = getWishlist();

    if (productIds.length === 0) {
      setProducts([]);
      setHasUnresolvedProducts(false);
      setStatus('empty');
      return;
    }

    setStatus('loading');

    try {
      const response = await wishlistApiRef.current.getWishlistProducts(
        ApiWishlistProductsRequestFromJSON({
          productIds
        })
      );

      if (requestId !== requestSequence.current) {
        return;
      }

      const resolvedProducts = response.items ?? [];
      const removableIds = response.removableIds ?? [];
      const unresolvedIds = response.unresolvedIds ?? [];

      setProducts(resolvedProducts);
      setHasUnresolvedProducts(unresolvedIds.length > 0);

      if (resolvedProducts.length > 0) {
        setStatus('ready');
      } else if (unresolvedIds.length > 0) {
        setStatus('error');
      } else {
        setStatus('empty');
      }

      if (removableIds.length > 0) {
        removeWishlistItems(removableIds);
      }
    } catch (error) {
      if (requestId !== requestSequence.current) {
        return;
      }

      console.error('Could not load Wishlist products.', error);

      setProducts([]);
      setHasUnresolvedProducts(false);
      setStatus('error');
    }
  }, [wishlistEnabled]);

  useEffect(() => {
    if (!wishlistEnabled) {
      return;
    }

    loadWishlist();

    return subscribeToWishlist(() => {
      loadWishlist();
    });
  }, [loadWishlist, wishlistEnabled]);

  if (!wishlistEnabled) {
    return null;
  }

  if (status === 'loading') {
    return (
      <output className="wishlist__status" aria-live="polite">
        {t(Keys.loading)}
      </output>
    );
  }

  if (status === 'empty') {
    return (
      <output className="wishlist__empty" aria-live="polite">
        {t(Keys.empty)}
      </output>
    );
  }

  if (status === 'error') {
    return (
      <div className="alert alert-warning" role="alert">
        {t(Keys.error)}
      </div>
    );
  }

  return (
    <section className="wishlist" aria-label={t(Keys.label)}>
      {hasUnresolvedProducts && (
        <output className="alert alert-warning">{t(Keys.partialError)}</output>
      )}
      <Row>
        {products.map(product => (
          <div className="col-12 col-md-6 col-xl-4 mb-4" key={product.id ?? product.detailUrl}>
            <ResultListItem
              productResultItem={product}
              showWishlistToggle
              onClick={() => {
                if (product.detailUrl) {
                  globalThis.location.assign(product.detailUrl);
                }
              }}
            />
          </div>
        ))}
      </Row>
    </section>
  );
};

export default Wishlist;
